import {
  Stack,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogContent,
  Backdrop,
  DialogTitle,
  DialogActions,
} from '@mui/material';
import {useClient} from '../../../../context/ClientContext';
import {useEffect, useState} from 'react';
import ClientButton from '../../common/Clientbutton';
import {PrivacyContent} from '../../../../pages/TermsAndPrivacy/privacyContent';
import {TermsOfServiceContent} from '../../../../pages/TermsAndPrivacy/termsOfServiceContent';
import {useTheme} from '@emotion/react';
import {useNavigate} from 'react-router-dom';
import {useClientApi} from '../../../../api/client';
import {CLIENT_TYPES} from '../../../../api/clientconfig';
import {convertFeetAndInchesToCentimeters, convertPoundsToKilograms} from '../../../../utils/conversions';
import {identifyClientType} from '../../../../utils/identifyClientType';

export default function TermCondition({
  userData,
  setUserData,
  updateDataInLocalStorage,
  setIsLoading,
  setGoBackTwoSteps,
}) {
  const [clientType, setClientType] = useState(CLIENT_TYPES.DEFAULT);

  useEffect(() => {
    setClientType(identifyClientType());
  }, []);

  const {theme: clientTheme, clientId} = useClient();
  const navigate = useNavigate();
  const clientApi = useClientApi(clientType);
  const theme = useTheme();
  const [isServerError, setIsServerError] = useState(false);
  const [isTermsChecked, setIsTermChecked] = useState({
    agreeTerm1: false,
    agreeTerm2: false,
    termsNotAgreedError: false,
  });
  const [openModalPrivacy, setOpenModalPrivacy] = useState(false);
  const [openModalTerms, setOpenModalTerms] = useState(false);

  // On this page, the back navigation checks the user's supplements list status
  // to determine whether to go back one or two steps.
  useEffect(() => {
    if (userData && userData.supplements && userData.supplements.length === 0) {
      setGoBackTwoSteps(true);
    }
  }, []);

  const commonTypographyStyles = {
    fontSize: {
      xs: '14px',
      md: '18px',
      sn: '18px',
      lg: '18px',
    },
    fontWeight: 400,
    lineHeight: '27px',
    padding: {
      lg: '0px 5px 0px 20px',
      sm: '0px 5px 0px 8px',
    },
  };

  const handleCheckboxChange = event => {
    const {name, checked} = event.target;

    // Update the specific checkbox
    setIsTermChecked(prevState => ({
      ...prevState,
      [name]: checked,
    }));
  };

  //Handles action to close terms of use modal dialog
  const closeTermsModal = isClose => {
    setOpenModalTerms(isClose);
  };

  //Handles action to close privacy policy modal dialog
  const closePrivacyPolicyModal = isClose => {
    setOpenModalPrivacy(isClose);
  };

  //Handles action to open privacy policy modal dialog
  const handlePrivacyPolicy = () => {
    setOpenModalPrivacy(true);
  };

  //Handles action to open terms of use modal dialog
  const handleTermsOfUse = () => {
    setOpenModalTerms(true);
  };

  const handleDataSubmit = async () => {
    // Handle height conversion and rounding
    const finalHeight = Math.round(
      userData.isFrontendHeightImperialUnits
        ? convertFeetAndInchesToCentimeters(Number(userData.height.ft), Number(userData.height.in))
        : Number(userData.height.cm),
    );

    // Handle weight conversion and rounding
    const finalWeight = Math.round(
      userData.isFrontendWeightImperialUnits
        ? convertPoundsToKilograms(Number(userData.weight))
        : Number(userData.weight),
    );

    const sessionId = localStorage.getItem('session-id');

    const extraDemographics = {
      birthDate: userData.dateOfBirth,
      gender: userData.genderId,
      ethinicity: userData.ethnicity,
      height: finalHeight,
      weight: finalWeight,
      isFrontendHeightImperialUnits: userData.isFrontendHeightImperialUnits,
      isFrontendWeightImperialUnits: userData.isFrontendWeightImperialUnits,
      isSmoke: userData.isSmoke,
      isDiabetes: userData.diabetes,
      hasHighBP: userData.isHighBP,
      hasHadStroke: userData.hasHadStroke,
      hasHadAttack: userData.hasHadHeartAttack,
      isOnBpMeds: userData.bpMeds,
      isPregnant: userData.isPregnant,
      menopause_status: userData.menopausalStatus,
      hasMedicalCondition: userData.medicalCondition?.length > 0 ? true : false,
      medicalConditionNames: userData.medicalCondition?.length ? userData.medicalCondition : null, // if no medical condition added, send null
      hasAllergies: userData.allergies?.length > 0 ? true : false,
      allergyNames: userData.allergies?.length > 0 ? userData.allergies : null, // if no allergy aded ,send null
      hasSupplements: userData.isTakingSupplements,
      supplements: userData.isTakingSupplements ? userData.supplements : null, // if select not taking supplement send null
      wellnessGoals: userData.wellnessGoal?.length > 0 ? userData.wellnessGoal : null,
    };

    let assessmentId = localStorage.getItem(`${clientId}-assessment-id`);

    try {
      setIsLoading(true);
      const response = await clientApi.postDemographicsNew(extraDemographics, assessmentId);

      if (response && response.data) {
        setIsLoading(false);
      } else {
        throw new Error('Invalid API response');
      }
    } catch (err) {
      setIsLoading(false);
      console.log(err);
    }
  };

  const sendSymptomDataToBakend = async () => {
    // const storedAssessmentId = localStorage.getItem(`${clientId}-assessment-id`);
    const sessionId = localStorage.getItem(`${clientId}-session-id`);
    const resultsToken = localStorage.getItem('results_token') || '';
    const isExperiencingHealthSymptomDec = localStorage.getItem('symptomDesc');
    const isExpirienceSymptom = localStorage.getItem('isExperiencingHealthSymptom') || false;

    if (isExpirienceSymptom && !isExperiencingHealthSymptomDec) {
      localStorage.removeItem('symptomDesc');
      localStorage.removeItem('conditions');
      await handleEmptySymptoms(sessionId, resultsToken);
      return;
    }
    if (isExpirienceSymptom && isExperiencingHealthSymptomDec) {
      if (!sessionId) {
        setIsServerError(true);
        return;
      }
      try {
        setIsLoading(true);
        const response = await clientApi.postSymptoms(sessionId, isExperiencingHealthSymptomDec, resultsToken);

        if (response.conditions) {
          localStorage.setItem('conditions', JSON.stringify(response.conditions));
        }
        setIsLoading(false);
      } catch (error) {
        console.error(error);
        setIsLoading(false);
        console.error(error);
        setIsServerError(true);
      }
    }
  };

  const handleEmptySymptoms = async (sessionId, resultsToken) => {
    if (!sessionId || !resultsToken) {
      setIsServerError(true);
      return;
    }
    try {
      setIsLoading(true);
      await clientApi.postSymptoms(sessionId, null, resultsToken);
      localStorage.removeItem('conditions');
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error(error);
      setIsServerError(true);
    }
  };

  const GetMyResultBtnhandler = async () => {
    console.log(userData);

    if (isTermsChecked.agreeTerm1 && isTermsChecked.agreeTerm2) {
      console.log(userData);
      await handleDataSubmit(); //wait for async call
      await sendSymptomDataToBakend(); // Wait for the async call to complete
      updateDataInLocalStorage();
      navigate(`/hellohealth-results`);
    } else {
      setIsTermChecked(prevState => ({
        ...prevState,
        termsNotAgreedError: true,
      }));
    }
  };

  return (
    <Stack
      // spacing={2}
      sx={{
        width: '100%',
        // maxWidth: '100%',
        maxWidth: {
          xs: '300px',
          sm: '390px',
          md: '400px',
          lg: '800px',
        },
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <Box>
        {/* 1st Terms checkbox */}
        <Box>
          <FormControlLabel
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
            }}
            control={
              <Checkbox
                name="agreeTerm1"
                checked={isTermsChecked.agreeTerm1}
                onChange={handleCheckboxChange}
                color="primary"
                sx={{
                  // padding: "2px 4px",
                  marginLeft: {lg: '12px', padding: '4px 8px'},
                  '&.MuiCheckbox-root.Mui-checked': {
                    color: clientTheme.primaryColor,
                  },
                  '&.MuiCheckbox-root.MuiCheckbox-indeterminate': {
                    color: clientTheme.primaryColor,
                  },
                }}
              />
            }
            label={
              <Box alignItems="center">
                <Typography sx={commonTypographyStyles} color="#000000">
                  I understand the information provided in this assessment is not intended for the diagnosis, treatment
                  and prevention of disease, but rather to provide me general information about my health and products
                  that may be able to improve my wellbeing.
                </Typography>
              </Box>
            }
            // Optional styling
          />
        </Box>

        {/* 2nd terms checkbox */}
        <Box
          sx={{
            pt: {
              xs: '8px',
              sm: '8px',
              md: '14px',
              lg: '14px',
            },
          }}
        >
          <FormControlLabel
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
            }}
            control={
              <Checkbox
                name="agreeTerm2"
                checked={isTermsChecked.agreeTerm2}
                onChange={handleCheckboxChange}
                color="primary"
                sx={{
                  // padding: "2px 4px",
                  marginLeft: {lg: '12px', padding: '4px 8px'},
                  '&.MuiCheckbox-root.Mui-checked': {
                    color: clientTheme.primaryColor,
                  },
                  '&.MuiCheckbox-root.MuiCheckbox-indeterminate': {
                    color: clientTheme.primaryColor,
                  },
                }}
              />
            }
            label={
              <Box alignItems="center">
                <Typography color="#000000" sx={commonTypographyStyles}>
                  I have read and understood the
                  <Button
                    onClick={handleTermsOfUse}
                    sx={{
                      color: clientTheme.primaryColor,
                      textDecoration: 'underline',
                      fontWeight: '400',
                      padding: '0px 6px',
                      fontSize: {
                        xs: '14px',
                        md: '18px',
                        sn: '18px',
                        lg: '18px',
                      },
                    }}
                  >
                    {' '}
                    terms of use
                  </Button>
                  <span
                    style={{
                      color: '#000000',
                      fontSize: {
                        xs: '14px',
                        md: '18px',
                        sn: '18px',
                        lg: '18px',
                      },
                    }}
                  >
                    {' '}
                    and
                  </span>
                  <Button
                    onClick={handlePrivacyPolicy}
                    sx={{
                      color: clientTheme.primaryColor,
                      textDecoration: 'underline',
                      fontWeight: '400',
                      padding: '0px 6px',
                      fontSize: {
                        xs: '14px',
                        md: '18px',
                        sn: '18px',
                        lg: '18px',
                      },
                    }}
                  >
                    {' '}
                    privacy policy
                  </Button>
                </Typography>
              </Box>
            }
          />
        </Box>
      </Box>
      <ClientButton
        text="Get My Results"
        onClick={GetMyResultBtnhandler}
        backgroundColor={clientTheme.primaryColor}
        sx={{
          maxWidth: '320px',
          width: '100%',
          marginTop: {xs: '24px', sm: '24px', md: '30px', lg: '40px'},
        }}
      ></ClientButton>
      {
        <Snackbar
          open={isTermsChecked.termsNotAgreedError}
          autoHideDuration={3000}
          onClose={() => {
            setIsTermChecked(prevState => ({
              ...prevState,
              termsNotAgreedError: false,
            }));
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Alert
            variant="filled"
            severity="error"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textAlign: 'center',
              mb: 8,
              fontSize: {
                xs: 16,
                sm: 18,
              },
              px: {
                xs: 2,
                sm: 5,
              },
              borderRadius: 3,
            }}
          >
            You must agree to terms and conditions to continue.
          </Alert>
        </Snackbar>
      }
      {
        <Snackbar
          open={isServerError}
          autoHideDuration={8000}
          onClose={() => setIsServerError(false)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
        >
          <Alert
            variant="filled"
            severity="error"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textAlign: 'center',
              mb: 8,
              fontSize: {
                xs: 16,
                sm: 18,
              },
              px: {
                xs: 2,
                sm: 5,
              },
              borderRadius: 3,
            }}
          >
            An error occurred.Please try again later.
          </Alert>
        </Snackbar>
      }
      {/* Modal box when user clicks terms of use link */}
      <Dialog
        open={openModalTerms}
        maxWidth="sm"
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        PaperProps={{
          sx: {
            borderRadius: '30px',
            position: 'absolute',
            mx: {
              xs: 2,
              md: 'auto',
            },
          },
        }}
        sx={{
          top: theme.spacing(5),
          paddingY: theme.spacing(2),
          '& .MuiDialog-paperScrollPaper': {
            borderRadius: 5,
          },
        }}
      >
        <DialogTitle sx={{paddingY: theme.spacing(3)}}>
          <Typography component="span" variant="h6">
            TERMS OF SERVICE AGREEMENT
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            paddingY: theme.spacing(5),
            paddingRight: theme.spacing(3),
          }}
        >
          <TermsOfServiceContent />
        </DialogContent>
        <DialogActions sx={{paddingBottom: theme.spacing(2)}}>
          <Button variant="text" onClick={() => closeTermsModal(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* Modal box when user clicks privacy policy link */}
      <Dialog
        open={openModalPrivacy}
        maxWidth="sm"
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        PaperProps={{
          sx: {
            borderRadius: '30px',
            position: 'absolute',
            mx: {
              xs: 2,
              md: 'auto',
            },
          },
        }}
        sx={{
          top: theme.spacing(5),
          paddingY: theme.spacing(2),
          '& .MuiDialog-paperScrollPaper': {
            borderRadius: 5,
          },
        }}
      >
        <DialogTitle sx={{paddingY: theme.spacing(3)}}>
          <Typography component="span" variant="h6">
            PRIVACY POLICY
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            paddingY: theme.spacing(5),
            paddingRight: theme.spacing(3),
          }}
        >
          <PrivacyContent isPage={false} />
        </DialogContent>
        <DialogActions sx={{paddingBottom: theme.spacing(2)}}>
          <Button variant="text" onClick={() => closePrivacyPolicyModal(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
